import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { storageService } from "@/application/services/StorageService";
import { LoggerFactory } from "@/application/logging/Logger";
import { secureStorage } from "@/utils/secureStorage";
import { StripeRepository } from "@/infrastructure/repositories/StripeRepository";

interface PaymentCompleteProps {
  canceled?: boolean;
  onFinish: (goTo?: string) => void;
}

export function PaymentComplete({
  canceled,
  onFinish
}: PaymentCompleteProps) {
  const [status, setStatus] = useState<"success" | "failed" | null>(null);
  const logger = LoggerFactory.getLogger();
  const stripeRepository = useMemo(() => new StripeRepository(), []);

  useEffect(() => {
    const verifySubscription = async () => {
      console.log("[PaymentComplete] verifySubscription:start", {
        href: window.location.href,
        search: window.location.search,
        canceledProp: canceled,
      });

      const params = new URLSearchParams(window.location.search);
      const sessionId =
        params.get("session_id") ||
        secureStorage.get<string>("stripe_checkout_session_id");

      console.log("[PaymentComplete] session lookup", {
        session_id_query: params.get("session_id"),
        session_id_storage: secureStorage.get<string>("stripe_checkout_session_id"),
        resolvedSessionId: sessionId,
      });

      if (!sessionId) {
        console.warn("[PaymentComplete] missing session id");
        setStatus("failed");
        toast.error("Invalid payment session.");
        return;
      }

      try {
        const checkoutResponse = await stripeRepository.getCheckoutSession(sessionId);
        console.log("[PaymentComplete] checkout session response", checkoutResponse);

        if (!checkoutResponse) {
          throw new Error("Unable to fetch checkout session");
        }

        const sessionEnvelope = checkoutResponse?.data ?? checkoutResponse ?? {};
        const stripeSession = sessionEnvelope?.stripe ?? sessionEnvelope ?? {};
        const transactionSession = sessionEnvelope?.transaction ?? {};
        const userId = localStorage.getItem("current_user_swimmer_id") ?? "";
        const paymentStatusRaw =
          stripeSession?.payment_status ||
          stripeSession?.status ||
          sessionEnvelope?.payment_status ||
          sessionEnvelope?.status ||
          sessionEnvelope?.checkout_session_status ||
          transactionSession?.payment_status ||
          transactionSession?.status ||
          "";
        const paymentStatus = String(paymentStatusRaw).toLowerCase();
        const isPaid =
          paymentStatus === "paid" ||
          paymentStatus === "complete" ||
          paymentStatus === "completed" ||
          paymentStatus === "succeeded";

        console.log("[PaymentComplete] parsed session", {
          userId,
          paymentStatus,
          isPaid,
          sessionEnvelope,
          stripeSession,
          transactionSession,
        });

        if (isPaid) {
          if (userId) {
            const saveSubscriptionResult = await stripeRepository.saveSubscription({
              user_id: String(userId),
              session_id: String(sessionId),
            });
            console.log("[PaymentComplete] saveSubscription result", {
              saveSubscriptionResult,
              userId,
              sessionId,
            });
          } else {
            console.warn("[PaymentComplete] missing userId; skipping saveSubscription");
          }

          setStatus("success");
          toast.success("Subscription verified");

          storageService.setItem("pro_subscription_active", "true");
          storageService.setItem("subscription_status", String(paymentStatus || "active"));
          if (stripeSession?.current_period_end) {
            storageService.setItem("subscription_expires_at", String(stripeSession.current_period_end));
          }
          if (stripeSession?.billing_cycle) {
            storageService.setItem("subscription_interval", String(stripeSession.billing_cycle));
          }
          secureStorage.remove("stripe_checkout_session_id");
        } else {
          console.warn("[PaymentComplete] payment not in paid state", {
            paymentStatus,
            canceledProp: canceled,
            sessionEnvelope,
            stripeSession,
            transactionSession,
          });
          setStatus("failed");
          secureStorage.remove("stripe_checkout_session_id");
          toast.error(canceled ? "Payment was canceled." : "Subscription is not active");
        }
      } catch (error) {
        console.error("[PaymentComplete] verifySubscription:error", error);
        logger.error("Subscription verification failed", error as Error, { sessionId });
        setStatus("failed");
        toast.error("Unable to verify subscription");
      }
    };

    verifySubscription();
  }, [canceled, logger, stripeRepository]);

  const handleConfirm = () => {
    onFinish("home");
  };

  const handleRetry = () => {
    onFinish();
  };

  return (
    <div className="w-full min-h-[70vh] flex items-center justify-center p-6">
      <div className="bg-white dark:bg-gray-900 shadow p-8 rounded-lg max-w-md w-full text-center">
        {status === null && (
          <p className="text-gray-600 dark:text-gray-300">
            Checking payment status...
          </p>
        )}

        {status === "success" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              Subscription Activated
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Your payment was successful and your subscription is now active. You can now access all premium features.
            </p>

            <button
              onClick={handleConfirm}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              Continue to Dashboard
            </button>
          </>
        )}

        {status === "failed" && (
          <>
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              {canceled ? "Payment Canceled" : "Payment Failed"}
            </h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {canceled
                ? "You canceled checkout before completing payment. No charges were made."
                : "Payment was not completed or subscription could not be verified."}
            </p>

            <button
              onClick={handleRetry}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
            >
              Return
            </button>
          </>
        )}
      </div>
    </div>
  );
}
